/**
 * AUTO-ADD Electric Summer Kit para gama SWFT
 * abce.store
 *
 * QUÉ HACE:
 * - Detecta cuántas unidades de productos de la colección "swft" hay en el carrito
 * - Mantiene la cantidad del "Electric Summer Kit" (variant 54450857673031) igual a esa cifra
 * - Si el cliente quita una bici SWFT, quita también su kit correspondiente
 * - El kit añadido automáticamente se marca con la propiedad _auto_kit para no tocar
 *   un kit que el cliente haya añadido manualmente por su cuenta (edge case, pero por si acaso)
 *
 * DÓNDE PEGARLO:
 * Admin > Online Store > Themes > Edit code > layout/theme.liquid
 * Pegar justo antes de </body>, envuelto en <script>...</script>
 *
 * HOOK CONFIRMADO (leyendo el export real del tema):
 * Este tema (arquitectura Web Components de Shopify) añade al carrito vía fetch()
 * a Theme.routes.cart_add_url (assets/product-form.js) y, tanto al añadir como al
 * cambiar/quitar cantidades en el carrito (assets/component-cart-items.js), dispara
 * un evento "cart:update" (ThemeEvents.cartUpdate, definido en assets/events.js) que
 * bubblea hasta document. Por eso basta un único listener de "cart:update".
 */
(function () {
  const SWFT_COLLECTION_HANDLE = 'swft';
  const KIT_VARIANT_ID = 54450857673031;
  const KIT_PROPERTY_FLAG = '_auto_kit';

  let swftProductIds = new Set();
  let swftLoaded = false;
  let reconciling = false;

  async function loadSwftCollection() {
    try {
      const res = await fetch(`/collections/${SWFT_COLLECTION_HANDLE}/products.json?limit=250`);
      if (!res.ok) throw new Error('No se pudo cargar la colección SWFT');
      const data = await res.json();
      swftProductIds = new Set(data.products.map((p) => p.id));
      swftLoaded = true;
    } catch (e) {
      console.error('[auto-kit-swft] Error cargando colección SWFT:', e);
    }
  }

  function isSwftProduct(productId) {
    return swftProductIds.has(productId);
  }

  async function getCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  async function reconcileKit() {
    if (!swftLoaded) await loadSwftCollection();
    if (reconciling) return; // evita carreras si se dispara dos veces seguidas
    reconciling = true;

    try {
      const cart = await getCart();

      let swftQty = 0;
      cart.items.forEach((item) => {
        if (isSwftProduct(item.product_id)) swftQty += item.quantity;
      });

      const kitLine = cart.items.find((item) => item.variant_id === KIT_VARIANT_ID);
      const kitQty = kitLine ? kitLine.quantity : 0;
      const kitIsAuto = kitLine && kitLine.properties && kitLine.properties[KIT_PROPERTY_FLAG] === 'true';

      if (swftQty > 0 && swftQty > kitQty) {
        // Añadir la diferencia (si no hay línea de kit aún, la crea; si hay, Shopify suma cantidad
        // solo si las properties coinciden, así que usamos /cart/change para fijar el total exacto
        // cuando ya existe una línea auto-añadida)
        if (kitLine && kitIsAuto) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: kitLine.key, quantity: swftQty }),
          });
        } else {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [
                {
                  id: KIT_VARIANT_ID,
                  quantity: swftQty - kitQty,
                  properties: { [KIT_PROPERTY_FLAG]: 'true' },
                },
              ],
            }),
          });
        }
      } else if (kitQty > swftQty && kitLine && kitIsAuto) {
        // Reducir (o eliminar si swftQty es 0) el kit auto-añadido para que cuadre
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: kitLine.key, quantity: swftQty }),
        });
      }

      // Avisa al resto del tema (drawer, mini-cart, contador) para que se repinte.
      // Usamos el MISMO evento real del tema ('cart:update'); como no incluimos
      // detail.data.sections, cart-items-component cae a su fallback de
      // re-renderizar la sección desde el servidor (visto en component-cart-items.js).
      // El guard `reconciling` (todavía true aquí) evita que esto nos vuelva a
      // disparar reconcileKit a nosotros mismos en bucle.
      const finalCart = await getCart();
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: finalCart,
            sourceId: 'auto-kit-swft',
            // IMPORTANTE: assets/cart-icon.js hace `event.detail.data?.itemCount ?? 0`
            // y, como el source NO es 'product-form-component', FIJA el contador a ese
            // valor absoluto. Sin itemCount el globo del carrito se ponía a 0 y se
            // ocultaba cada vez que reconciliábamos el kit.
            data: { source: 'auto-kit-swft', itemCount: finalCart.item_count },
          },
        })
      );
    } catch (e) {
      console.error('[auto-kit-swft] Error reconciliando el kit:', e);
    } finally {
      reconciling = false;
    }
  }

  function init() {
    loadSwftCollection();

    // Hook: evento real de este tema (assets/events.js -> ThemeEvents.cartUpdate = 'cart:update').
    // Se dispara tanto al añadir (product-form.js) como al cambiar/quitar cantidades
    // en el carrito (component-cart-items.js). Bubblea hasta document, así que lo
    // capturamos aquí en un único sitio.
    document.addEventListener('cart:update', () => {
      if (!reconciling) reconcileKit();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
